/**
 * Vehicle model + state machine (spec sections 9 & 10).
 *
 * Pure serializable data. Positions are in grid-tile units (a tile at column
 * tx / row ty has its center at (tx, ty)); the renderer converts to pixels.
 * The engine advances vehicles; the scene only draws them.
 */

export const VEHICLE_STATE = {
  Entering: 'entering',
  Searching: 'searching',
  DrivingToSpace: 'driving-to-space',
  Parked: 'parked',
  LeavingSpace: 'leaving-space',
  Paying: 'paying',
  Exiting: 'exiting',
  Left: 'left',
  Abandoned: 'abandoned',
} as const;

export type VehicleState = (typeof VEHICLE_STATE)[keyof typeof VEHICLE_STATE];

export interface PathPoint {
  x: number;
  y: number;
}

export interface Vehicle {
  id: string;
  typeId: string;
  state: VehicleState;

  /** Continuous position in tile units. */
  x: number;
  y: number;

  /** Waypoints (tile centers) the vehicle is currently following. */
  path: PathPoint[];
  pathIndex: number;

  /** Assigned parking-space tile index (null until/if one is reserved). */
  targetSpace: number | null;

  /** Remaining parked time, in game-minutes. */
  parkedRemainingMinutes: number;
  /** Remaining service time at the payment point, in game-minutes. */
  payingRemainingMinutes: number;
  /** Whether the parking fee has been collected this visit. */
  paid: boolean;
  /** Arrival order in the exit payment queue (lower = earlier). */
  enteredQueueAt: number;
  /** Tile index of the exit this car is queuing at (-1 until queued). */
  queueExitIndex: number;
  /** Time spent waiting in the payment queue, in game-minutes. */
  queueWaitMinutes: number;
  /** How long the car has been searching, in game-minutes. */
  searchElapsedMinutes: number;

  /** Total parking duration this visit was assigned (for metrics). */
  plannedDurationMinutes: number;

  speed: number; // tiles per game-minute
  spawnedAtMinute: number;
}

export interface CreateVehicleOptions {
  id: string;
  typeId: string;
  x: number;
  y: number;
  speed: number;
  spawnedAtMinute: number;
  plannedDurationMinutes: number;
}

export function createVehicle(options: CreateVehicleOptions): Vehicle {
  return {
    id: options.id,
    typeId: options.typeId,
    state: VEHICLE_STATE.Entering,
    x: options.x,
    y: options.y,
    path: [],
    pathIndex: 0,
    targetSpace: null,
    parkedRemainingMinutes: 0,
    payingRemainingMinutes: 0,
    paid: false,
    enteredQueueAt: 0,
    queueExitIndex: -1,
    queueWaitMinutes: 0,
    searchElapsedMinutes: 0,
    plannedDurationMinutes: options.plannedDurationMinutes,
    speed: options.speed,
    spawnedAtMinute: options.spawnedAtMinute,
  };
}

/** States in which the vehicle is holding (reserving) its target space. */
export function isOccupyingSpace(v: Vehicle): boolean {
  return (
    v.targetSpace !== null &&
    (v.state === VEHICLE_STATE.DrivingToSpace ||
      v.state === VEHICLE_STATE.Parked)
  );
}
